// IndexedDB wrapper for offline-first data persistence
import Dexie from 'dexie';

// Initialize the database
export const db = new Dexie('TransSaharaDB');

// Define schema - Version 7 adds Station attendants table
db.version(7).stores({
  trucks: 'id, matricule, company_id, current_status, updated_at',
  drivers: 'id, phone, active, updated_at',
  companies: 'id, name, updated_at',
  operations: 'id, code, type, active, updated_at',
  trips:
    'id, truck_id, driver_id, operation_id, status, created_at, updated_at, order_id',
  tripUpdates: 'id, trip_id, status, created_at',
  clients: 'id, name, type, status, phone, updated_at',
  orders: 'id, client_id, status, type, order_date, updated_at',
  invoices:
    'id, client_id, invoice_number, status, invoice_date, due_date, updated_at',
  stockMovements:
    'id, movement_type, trip_id, order_id, movement_date, created_at',
  pricingRules: 'id, client_id, client_type, is_active, created_at',
  stations: 'id, code, status, updated_at',
  stationTanks: 'id, station_id, product, is_active, updated_at',
  stationPumps: 'id, station_id, tank_id, is_active, updated_at',
  stationAttendants: 'id, station_id, is_active, updated_at',
  stationPrices: 'id, station_id, product, effective_from, created_at',
  stationPurchases: 'id, station_id, tank_id, purchase_date, created_at',
  stationSales: 'id, station_id, tank_id, shift_id, sale_date, created_at',
  stationShifts: 'id, station_id, status, opened_at, updated_at',
  stationExpenses: 'id, station_id, shift_id, expense_date, created_at',
  syncQueue:
    '++id, entity_type, action, priority, synced, client_timestamp, [status+priority]',
  appState: 'key',
});

// Version 8: the sync queue gains a real state machine.
// `priority` and `[status+priority]` were indexed but never written, so the compound
// index was unusable. We drop them and index `status` instead, which lets getPending()
// hit an index rather than scanning the whole table every 10s (see SyncContext).
db.version(8)
  .stores({
    syncQueue: '++id, entity_type, action, status, client_timestamp',
  })
  .upgrade(async (tx) =>
    tx
      .table('syncQueue')
      .toCollection()
      .modify((item) => {
        item.status = item.synced === true ? SYNC_STATUS.SYNCED : SYNC_STATUS.PENDING;
        if (typeof item.attempts !== 'number') item.attempts = 0;
        delete item.priority;
      }),
  );

// Version 9: le modèle de lecture local (« hors ligne local d'abord »).
//  - records: une ligne par entité (type + id), telle que le serveur la renvoie;
//    les écritures locales la modifient aussitôt, la synchronisation la confirme.
//  - httpCache: dernière réponse connue d'une lecture (ordre des pages, agrégats).
//  - idMap: identifiant créé sur l'appareil -> identifiant attribué par le serveur.
db.version(9).stores({
  records: '[kind+id], kind, ts',
  httpCache: 'key, path, ts',
  idMap: 'localId',
});

// Version 10: fichiers consultés (PDF, rapports, photos), relus sans réseau.
db.version(10).stores({
  fichiers: 'cle, ts',
});

// Queue item lifecycle. An item is only ever removed from the queue once the server
// acknowledged it (SYNCED) or once we gave up on it (FAILED, kept visible to the user).
export const SYNC_STATUS = {
  PENDING: 'pending',
  SYNCED: 'synced',
  FAILED: 'failed',
};

// After this many consecutive rejections an item is parked in FAILED so it stops
// being re-sent on every single sync round (it used to be retried forever).
export const MAX_SYNC_ATTEMPTS = 5;

// A globally unique id for a queued item. crypto.randomUUID needs a secure
// context, which a LAN deployment over plain http is not, so fall back rather
// than throw — an item without an id would lose its replay protection.
const newClientId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `q-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const toNumericIds = (ids) =>
  (ids || []).reduce((acc, id) => {
    const num = typeof id === 'string' ? Number(id) : id;
    if (Number.isFinite(num)) acc.push(num);
    return acc;
  }, []);

// Portée de l'utilisateur connecté (société + compte). Les éléments de file d'un
// autre compte restent sur l'appareil mais ne sont ni comptés ni rejoués.
let portee = null;
export const definirPorteeFile = (valeur) => {
  portee = valeur || null;
};
export const porteeFile = () => portee;
const dansLaPortee = (item) => !item.scope || !portee || item.scope === portee;

// Sync queue operations
export const syncQueue = {
  async add(entityType, action, data) {
    try {
      const item = {
        // Dexie's `++id` restarts at 1 on every device and after every local
        // reset, so it cannot identify an item to the server: two workstations
        // in the same organisation would both claim item 1 and cancel each
        // other out in the idempotency table. This id is globally unique and is
        // what the server keys replay protection on.
        client_id: newClientId(),
        entity_type: entityType,
        action,
        data,
        client_timestamp: new Date().toISOString(),
        scope: portee,
        synced: false,
        status: SYNC_STATUS.PENDING,
        attempts: 0,
        last_error: null,
      };
      const id = await db.syncQueue.add(item);
      return { ...item, id };
    } catch (err) {
      console.warn('Failed to add to sync queue:', err);
      return null;
    }
  },

  // Ordered by insertion id: creates must reach the server before the updates
  // that depend on them. `limit` lets the caller send bounded batches.
  async getPending(limit = null) {
    try {
      const collection = db.syncQueue
        .where('status')
        .equals(SYNC_STATUS.PENDING);
      const items = (await collection.toArray()).filter(dansLaPortee).sort((a, b) => a.id - b.id);
      return limit ? items.slice(0, limit) : items;
    } catch (err) {
      console.warn('Failed to get pending sync items:', err);
      return [];
    }
  },

  async get(id) {
    try {
      return (await db.syncQueue.get(id)) || null;
    } catch (err) {
      return null;
    }
  },

  async countPending() {
    try {
      if (!portee) return await db.syncQueue.where('status').equals(SYNC_STATUS.PENDING).count();
      return (await db.syncQueue.where('status').equals(SYNC_STATUS.PENDING).toArray()).filter(dansLaPortee).length;
    } catch (err) {
      console.warn('Failed to count pending sync items:', err);
      return 0;
    }
  },

  // Items we gave up on (conflicts, or too many attempts). Surfaced in the queue modal
  // so an operator can inspect and discard them instead of silently losing data.
  async getFailed() {
    try {
      const items = await db.syncQueue.where('status').equals(SYNC_STATUS.FAILED).toArray();
      return items.filter(dansLaPortee).sort((a, b) => a.id - b.id);
    } catch (err) {
      console.warn('Failed to get failed sync items:', err);
      return [];
    }
  },

  // Records a rejection for these items. Definitive rejections (server-side conflicts)
  // are parked immediately; transient ones only after MAX_SYNC_ATTEMPTS.
  async markAttemptFailed(ids, { reason = null, definitive = false } = {}) {
    const safeIds = toNumericIds(ids);
    if (safeIds.length === 0) return;
    try {
      await db.syncQueue
        .where('id')
        .anyOf(safeIds)
        .modify((item) => {
          item.attempts = (item.attempts || 0) + 1;
          item.last_error = reason;
          if (definitive || item.attempts >= MAX_SYNC_ATTEMPTS) {
            item.status = SYNC_STATUS.FAILED;
          }
        });
    } catch (err) {
      console.warn('Failed to mark sync attempt as failed:', err);
    }
  },

  // Manual discard of an item the operator judges unrecoverable.
  async remove(id) {
    const [safeId] = toNumericIds([id]);
    if (safeId === undefined) return false;
    try {
      await db.syncQueue.delete(safeId);
      return true;
    } catch (err) {
      console.warn('Failed to remove sync queue item:', err);
      return false;
    }
  },

  // Put a parked item back in the pending lane (attempt counter reset).
  async retry(id) {
    const [safeId] = toNumericIds([id]);
    if (safeId === undefined) return false;
    try {
      await db.syncQueue.update(safeId, {
        status: SYNC_STATUS.PENDING,
        attempts: 0,
        last_error: null,
      });
      return true;
    } catch (err) {
      console.warn('Failed to retry sync queue item:', err);
      return false;
    }
  },

  async clearFailed() {
    try {
      const failed = await db.syncQueue.where('status').equals(SYNC_STATUS.FAILED).primaryKeys();
      if (failed.length > 0) await db.syncQueue.bulkDelete(failed);
      return failed.length;
    } catch (err) {
      console.warn('Failed to clear failed sync items:', err);
      return 0;
    }
  },

  async markSynced(ids) {
    try {
      const safeIds = toNumericIds(ids);
      if (safeIds.length > 0) {
        try {
          await db.syncQueue
            .where('id')
            .anyOf(safeIds)
            .modify({ synced: true, status: SYNC_STATUS.SYNCED });
        } catch (innerErr) {
          console.warn('Primary markSynced failed, falling back:', innerErr);
          const items = await db.syncQueue.bulkGet(safeIds);
          const updated = items
            .reduce((acc, item) => {
              if (item) acc.push({ ...item, synced: true, status: SYNC_STATUS.SYNCED });
              return acc;
            }, []);
          if (updated.length > 0) await db.syncQueue.bulkPut(updated);
        }
      }
    } catch (err) {
      console.warn('Failed to mark items as synced:', err);
    }
  },

  async clearSynced() {
    try {
      const syncedIds = await db.syncQueue
        .where('status')
        .equals(SYNC_STATUS.SYNCED)
        .primaryKeys();
      if (syncedIds.length > 0) {
        await db.syncQueue.bulkDelete(syncedIds);
      }
    } catch (err) {
      console.warn('Failed to clear synced items:', err);
    }
  },
};

// App state operations
export const appState = {
  async get(key) {
    try {
      const item = await db.appState.get(key);
      return item?.value;
    } catch (err) {
      console.warn('Failed to get app state:', err);
      return null;
    }
  },

  async set(key, value) {
    try {
      await db.appState.put({ key, value });
    } catch (err) {
      console.warn('Failed to set app state:', err);
    }
  },
};

// Truck operations
export const trucksDB = {
  async getAll() {
    try {
      return await db.trucks.toArray();
    } catch (err) {
      console.warn('Failed to get trucks:', err);
      return [];
    }
  },

  async getById(id) {
    try {
      return await db.trucks.get(id);
    } catch (err) {
      console.warn('Failed to get truck:', err);
      return null;
    }
  },

  async getByMatricule(matricule) {
    try {
      return await db.trucks
        .where('matricule')
        .equals(matricule.toUpperCase())
        .first();
    } catch (err) {
      console.warn('Failed to get truck by matricule:', err);
      return null;
    }
  },

  async search(query) {
    try {
      const q = query.toUpperCase();
      return await db.trucks
        .filter((truck) => truck.matricule && truck.matricule.includes(q))
        .limit(10)
        .toArray();
    } catch (err) {
      console.warn('Failed to search trucks:', err);
      return [];
    }
  },

  async upsert(truck) {
    try {
      await db.trucks.put(truck);
    } catch (err) {
      console.warn('Failed to upsert truck:', err);
    }
  },

  async bulkUpsert(trucks) {
    try {
      if (trucks && trucks.length > 0) {
        await db.trucks.bulkPut(trucks);
      }
    } catch (err) {
      console.warn('Failed to bulk upsert trucks:', err);
    }
  },

  async deleteById(id) {
    try {
      await db.trucks.delete(id);
    } catch (err) {
      console.warn('Failed to delete truck:', err);
    }
  },
};

// Driver operations
export const driversDB = {
  async getAll() {
    try {
      return await db.drivers.toArray();
    } catch (err) {
      console.warn('Failed to get drivers:', err);
      return [];
    }
  },

  async getById(id) {
    try {
      return await db.drivers.get(id);
    } catch (err) {
      console.warn('Failed to get driver:', err);
      return null;
    }
  },

  async getActive() {
    try {
      return await db.drivers.where('active').equals(true).toArray();
    } catch (err) {
      console.warn('Failed to get active drivers:', err);
      return [];
    }
  },

  async getByPhone(phone) {
    try {
      if (!phone) return null;
      return await db.drivers
        .where('phone')
        .equals(String(phone).trim())
        .first();
    } catch (err) {
      console.warn('Failed to get driver by phone:', err);
      return null;
    }
  },

  async upsert(driver) {
    try {
      await db.drivers.put(driver);
    } catch (err) {
      console.warn('Failed to upsert driver:', err);
    }
  },

  async bulkUpsert(drivers) {
    try {
      if (drivers && drivers.length > 0) {
        await db.drivers.bulkPut(drivers);
      }
    } catch (err) {
      console.warn('Failed to bulk upsert drivers:', err);
    }
  },

  async deleteById(id) {
    try {
      await db.drivers.delete(id);
    } catch (err) {
      console.warn('Failed to delete driver:', err);
    }
  },
};

// Company operations
export const companiesDB = {
  async getAll() {
    try {
      return await db.companies.orderBy('name').toArray();
    } catch (err) {
      console.warn('Failed to get companies:', err);
      return [];
    }
  },

  async getById(id) {
    try {
      return await db.companies.get(id);
    } catch (err) {
      console.warn('Failed to get company:', err);
      return null;
    }
  },

  async upsert(company) {
    try {
      await db.companies.put(company);
    } catch (err) {
      console.warn('Failed to upsert company:', err);
    }
  },

  async bulkUpsert(companies) {
    try {
      if (companies && companies.length > 0) {
        await db.companies.bulkPut(companies);
      }
    } catch (err) {
      console.warn('Failed to bulk upsert companies:', err);
    }
  },

  async deleteById(id) {
    try {
      await db.companies.delete(id);
    } catch (err) {
      console.warn('Failed to delete company:', err);
    }
  },
};

// Operation operations (replaces partners)
export const operationsDB = {
  async getAll() {
    try {
      return await db.operations.toArray();
    } catch (err) {
      console.warn('Failed to get operations:', err);
      return [];
    }
  },

  async getById(id) {
    try {
      return await db.operations.get(id);
    } catch (err) {
      console.warn('Failed to get operation:', err);
      return null;
    }
  },

  async getActive() {
    try {
      return await db.operations.where('active').equals(true).toArray();
    } catch (err) {
      console.warn('Failed to get active operations:', err);
      return [];
    }
  },

  async search(query) {
    try {
      const q = query.toUpperCase();
      return await db.operations
        .filter(
          (op) =>
            (op.code && op.code.includes(q)) ||
            (op.beneficiary_name &&
              op.beneficiary_name.toUpperCase().includes(q)),
        )
        .limit(10)
        .toArray();
    } catch (err) {
      console.warn('Failed to search operations:', err);
      return [];
    }
  },

  async upsert(operation) {
    try {
      await db.operations.put(operation);
    } catch (err) {
      console.warn('Failed to upsert operation:', err);
    }
  },

  async bulkUpsert(operations) {
    try {
      if (operations && operations.length > 0) {
        await db.operations.bulkPut(operations);
      }
    } catch (err) {
      console.warn('Failed to bulk upsert operations:', err);
    }
  },

  async deleteById(id) {
    try {
      await db.operations.delete(id);
    } catch (err) {
      console.warn('Failed to delete operation:', err);
    }
  },
};

// Trip operations
export const tripsDB = {
  async getAll() {
    try {
      return await db.trips.orderBy('created_at').reverse().toArray();
    } catch (err) {
      console.warn('Failed to get trips:', err);
      return [];
    }
  },

  async getById(id) {
    try {
      return await db.trips.get(id);
    } catch (err) {
      console.warn('Failed to get trip:', err);
      return null;
    }
  },

  async getActive() {
    try {
      const completedStatuses = ['COMPLETED', 'CANCELLED'];
      return await db.trips
        .filter((trip) => !completedStatuses.includes(trip.status))
        .toArray();
    } catch (err) {
      console.warn('Failed to get active trips:', err);
      return [];
    }
  },

  async getByTruckId(truckId) {
    try {
      return await db.trips
        .where('truck_id')
        .equals(truckId)
        .reverse()
        .toArray();
    } catch (err) {
      console.warn('Failed to get trips by truck:', err);
      return [];
    }
  },

  async upsert(trip) {
    try {
      await db.trips.put(trip);
    } catch (err) {
      console.warn('Failed to upsert trip:', err);
    }
  },

  async bulkUpsert(trips) {
    try {
      if (trips && trips.length > 0) {
        await db.trips.bulkPut(trips);
      }
    } catch (err) {
      console.warn('Failed to bulk upsert trips:', err);
    }
  },

  async deleteById(id) {
    try {
      await db.trips.delete(id);
    } catch (err) {
      console.warn('Failed to delete trip:', err);
    }
  },

  async deleteByTruckId(truckId) {
    try {
      await db.trips.where('truck_id').equals(truckId).delete();
    } catch (err) {
      console.warn('Failed to delete trips by truck:', err);
    }
  },

  async deleteByDriverId(driverId) {
    try {
      await db.trips.where('driver_id').equals(driverId).delete();
    } catch (err) {
      console.warn('Failed to delete trips by driver:', err);
    }
  },

  async deleteByOperationId(operationId) {
    try {
      await db.trips.where('operation_id').equals(operationId).delete();
    } catch (err) {
      console.warn('Failed to delete trips by operation:', err);
    }
  },
};

// Trip update operations
export const tripUpdatesDB = {
  async getByTripId(tripId) {
    try {
      return await db.tripUpdates
        .where('trip_id')
        .equals(tripId)
        .sortBy('created_at');
    } catch (err) {
      console.warn('Failed to get trip updates:', err);
      return [];
    }
  },

  async add(update) {
    try {
      await db.tripUpdates.put(update);
    } catch (err) {
      console.warn('Failed to add trip update:', err);
    }
  },

  async bulkUpsert(updates) {
    try {
      if (updates && updates.length > 0) {
        await db.tripUpdates.bulkPut(updates);
      }
    } catch (err) {
      console.warn('Failed to bulk upsert trip updates:', err);
    }
  },

  async deleteByTripId(tripId) {
    try {
      await db.tripUpdates.where('trip_id').equals(tripId).delete();
    } catch (err) {
      console.warn('Failed to delete trip updates:', err);
    }
  },
};

// Client operations
export const clientsDB = {
  async getAll() {
    try {
      return await db.clients.orderBy('name').toArray();
    } catch (err) {
      console.warn('Failed to get clients:', err);
      return [];
    }
  },

  async getById(id) {
    try {
      return await db.clients.get(id);
    } catch (err) {
      console.warn('Failed to get client:', err);
      return null;
    }
  },

  async getActive() {
    try {
      return await db.clients.where('status').equals('ACTIVE').toArray();
    } catch (err) {
      console.warn('Failed to get active clients:', err);
      return [];
    }
  },

  async upsert(client) {
    try {
      await db.clients.put(client);
    } catch (err) {
      console.warn('Failed to upsert client:', err);
    }
  },

  async bulkUpsert(clients) {
    try {
      if (clients && clients.length > 0) {
        await db.clients.bulkPut(clients);
      }
    } catch (err) {
      console.warn('Failed to bulk upsert clients:', err);
    }
  },

  async deleteById(id) {
    try {
      await db.clients.delete(id);
    } catch (err) {
      console.warn('Failed to delete client:', err);
    }
  },
};

// Order operations
export const ordersDB = {
  async getAll() {
    try {
      return await db.orders.orderBy('order_date').reverse().toArray();
    } catch (err) {
      console.warn('Failed to get orders:', err);
      return [];
    }
  },

  async getById(id) {
    try {
      return await db.orders.get(id);
    } catch (err) {
      console.warn('Failed to get order:', err);
      return null;
    }
  },

  async getByClientId(clientId) {
    try {
      return await db.orders
        .where('client_id')
        .equals(clientId)
        .reverse()
        .toArray();
    } catch (err) {
      console.warn('Failed to get orders by client:', err);
      return [];
    }
  },

  async getActive() {
    try {
      const completedStatuses = ['DELIVERED', 'INVOICED', 'CANCELLED'];
      return await db.orders
        .filter((order) => !completedStatuses.includes(order.status))
        .toArray();
    } catch (err) {
      console.warn('Failed to get active orders:', err);
      return [];
    }
  },

  async upsert(order) {
    try {
      await db.orders.put(order);
    } catch (err) {
      console.warn('Failed to upsert order:', err);
    }
  },

  async bulkUpsert(orders) {
    try {
      if (orders && orders.length > 0) {
        await db.orders.bulkPut(orders);
      }
    } catch (err) {
      console.warn('Failed to bulk upsert orders:', err);
    }
  },

  async deleteById(id) {
    try {
      await db.orders.delete(id);
    } catch (err) {
      console.warn('Failed to delete order:', err);
    }
  },
};

// Invoice operations
export const invoicesDB = {
  async getAll() {
    try {
      return await db.invoices.orderBy('invoice_date').reverse().toArray();
    } catch (err) {
      console.warn('Failed to get invoices:', err);
      return [];
    }
  },

  async getById(id) {
    try {
      return await db.invoices.get(id);
    } catch (err) {
      console.warn('Failed to get invoice:', err);
      return null;
    }
  },

  async getByClientId(clientId) {
    try {
      return await db.invoices
        .where('client_id')
        .equals(clientId)
        .reverse()
        .toArray();
    } catch (err) {
      console.warn('Failed to get invoices by client:', err);
      return [];
    }
  },

  async getUnpaid() {
    try {
      const unpaidStatuses = ['SENT', 'PARTIALLY_PAID', 'OVERDUE'];
      return await db.invoices
        .filter((invoice) => unpaidStatuses.includes(invoice.status))
        .toArray();
    } catch (err) {
      console.warn('Failed to get unpaid invoices:', err);
      return [];
    }
  },

  async upsert(invoice) {
    try {
      await db.invoices.put(invoice);
    } catch (err) {
      console.warn('Failed to upsert invoice:', err);
    }
  },

  async bulkUpsert(invoices) {
    try {
      if (invoices && invoices.length > 0) {
        await db.invoices.bulkPut(invoices);
      }
    } catch (err) {
      console.warn('Failed to bulk upsert invoices:', err);
    }
  },

  async deleteById(id) {
    try {
      await db.invoices.delete(id);
    } catch (err) {
      console.warn('Failed to delete invoice:', err);
    }
  },
};

// Stock movement operations
export const stockMovementsDB = {
  async getAll() {
    try {
      return await db.stockMovements
        .orderBy('movement_date')
        .reverse()
        .toArray();
    } catch (err) {
      console.warn('Failed to get stock movements:', err);
      return [];
    }
  },

  async getByOrderId(orderId) {
    try {
      return await db.stockMovements
        .where('order_id')
        .equals(orderId)
        .toArray();
    } catch (err) {
      console.warn('Failed to get stock movements by order:', err);
      return [];
    }
  },

  async getByTripId(tripId) {
    try {
      return await db.stockMovements.where('trip_id').equals(tripId).toArray();
    } catch (err) {
      console.warn('Failed to get stock movements by trip:', err);
      return [];
    }
  },

  async getById(id) {
    try {
      return await db.stockMovements.get(id);
    } catch (err) {
      console.warn('Failed to get stock movement by id:', err);
      return null;
    }
  },

  async upsert(movement) {
    try {
      await db.stockMovements.put(movement);
    } catch (err) {
      console.warn('Failed to upsert stock movement:', err);
    }
  },

  async bulkUpsert(movements) {
    try {
      if (movements && movements.length > 0) {
        await db.stockMovements.bulkPut(movements);
      }
    } catch (err) {
      console.warn('Failed to bulk upsert stock movements:', err);
    }
  },

  async deleteById(id) {
    try {
      await db.stockMovements.delete(id);
    } catch (err) {
      console.warn('Failed to delete stock movement:', err);
    }
  },
};

// Pricing rules operations
export const pricingRulesDB = {
  async getAll() {
    try {
      return await db.pricingRules.toArray();
    } catch (err) {
      console.warn('Failed to get pricing rules:', err);
      return [];
    }
  },

  async getActive() {
    try {
      return await db.pricingRules.where('is_active').equals(true).toArray();
    } catch (err) {
      console.warn('Failed to get active pricing rules:', err);
      return [];
    }
  },

  async bulkUpsert(rules) {
    try {
      if (rules && rules.length > 0) {
        await db.pricingRules.bulkPut(rules);
      }
    } catch (err) {
      console.warn('Failed to bulk upsert pricing rules:', err);
    }
  },
};

// Station Service operations
export const stationsDB = {
  async getAll() {
    try {
      return await db.stations.orderBy('updated_at').reverse().toArray();
    } catch (err) {
      console.warn('Failed to get stations:', err);
      return [];
    }
  },

  async getById(id) {
    try {
      return await db.stations.get(id);
    } catch (err) {
      console.warn('Failed to get station by id:', err);
      return null;
    }
  },

  async upsert(item) {
    try {
      await db.stations.put(item);
    } catch (err) {
      console.warn('Failed to upsert station:', err);
    }
  },

  async bulkUpsert(items) {
    try {
      if (items && items.length > 0) await db.stations.bulkPut(items);
    } catch (err) {
      console.warn('Failed to bulk upsert stations:', err);
    }
  },

  async deleteById(id) {
    try {
      await db.stations.delete(id);
    } catch (err) {
      console.warn('Failed to delete station:', err);
    }
  },
};

export const stationTanksDB = {
  async getByStationId(stationId) {
    try {
      return await db.stationTanks.where('station_id').equals(stationId).toArray();
    } catch (err) {
      console.warn('Failed to get station tanks:', err);
      return [];
    }
  },

  async upsert(item) {
    try {
      await db.stationTanks.put(item);
    } catch (err) {
      console.warn('Failed to upsert station tank:', err);
    }
  },

  async bulkUpsert(items) {
    try {
      if (items && items.length > 0) await db.stationTanks.bulkPut(items);
    } catch (err) {
      console.warn('Failed to bulk upsert station tanks:', err);
    }
  },
};

export const stationPumpsDB = {
  async getByStationId(stationId) {
    try {
      return await db.stationPumps.where('station_id').equals(stationId).toArray();
    } catch (err) {
      console.warn('Failed to get station pumps:', err);
      return [];
    }
  },

  async upsert(item) {
    try {
      await db.stationPumps.put(item);
    } catch (err) {
      console.warn('Failed to upsert station pump:', err);
    }
  },

  async bulkUpsert(items) {
    try {
      if (items && items.length > 0) await db.stationPumps.bulkPut(items);
    } catch (err) {
      console.warn('Failed to bulk upsert station pumps:', err);
    }
  },
};

export const stationAttendantsDB = {
  async getByStationId(stationId) {
    try {
      return await db.stationAttendants
        .where('station_id')
        .equals(stationId)
        .toArray();
    } catch (err) {
      console.warn('Failed to get station attendants:', err);
      return [];
    }
  },

  async upsert(item) {
    try {
      await db.stationAttendants.put(item);
    } catch (err) {
      console.warn('Failed to upsert station attendant:', err);
    }
  },

  async bulkUpsert(items) {
    try {
      if (items && items.length > 0) await db.stationAttendants.bulkPut(items);
    } catch (err) {
      console.warn('Failed to bulk upsert station attendants:', err);
    }
  },
};

export const stationPricesDB = {
  async getByStationId(stationId) {
    try {
      return await db.stationPrices
        .where('station_id')
        .equals(stationId)
        .reverse()
        .sortBy('effective_from');
    } catch (err) {
      console.warn('Failed to get station prices:', err);
      return [];
    }
  },

  async upsert(item) {
    try {
      await db.stationPrices.put(item);
    } catch (err) {
      console.warn('Failed to upsert station price:', err);
    }
  },

  async bulkUpsert(items) {
    try {
      if (items && items.length > 0) await db.stationPrices.bulkPut(items);
    } catch (err) {
      console.warn('Failed to bulk upsert station prices:', err);
    }
  },
};

export const stationPurchasesDB = {
  async getByStationId(stationId) {
    try {
      return await db.stationPurchases
        .where('station_id')
        .equals(stationId)
        .reverse()
        .sortBy('purchase_date');
    } catch (err) {
      console.warn('Failed to get station purchases:', err);
      return [];
    }
  },

  async upsert(item) {
    try {
      await db.stationPurchases.put(item);
    } catch (err) {
      console.warn('Failed to upsert station purchase:', err);
    }
  },

  async bulkUpsert(items) {
    try {
      if (items && items.length > 0) await db.stationPurchases.bulkPut(items);
    } catch (err) {
      console.warn('Failed to bulk upsert station purchases:', err);
    }
  },
};

export const stationSalesDB = {
  async getByStationId(stationId) {
    try {
      return await db.stationSales
        .where('station_id')
        .equals(stationId)
        .reverse()
        .sortBy('sale_date');
    } catch (err) {
      console.warn('Failed to get station sales:', err);
      return [];
    }
  },

  async upsert(item) {
    try {
      await db.stationSales.put(item);
    } catch (err) {
      console.warn('Failed to upsert station sale:', err);
    }
  },

  async bulkUpsert(items) {
    try {
      if (items && items.length > 0) await db.stationSales.bulkPut(items);
    } catch (err) {
      console.warn('Failed to bulk upsert station sales:', err);
    }
  },
};

export const stationShiftsDB = {
  async getByStationId(stationId) {
    try {
      return await db.stationShifts
        .where('station_id')
        .equals(stationId)
        .reverse()
        .sortBy('opened_at');
    } catch (err) {
      console.warn('Failed to get station shifts:', err);
      return [];
    }
  },

  async getOpenByStationId(stationId) {
    try {
      const shifts = await db.stationShifts
        .where('station_id')
        .equals(stationId)
        .and((item) => item.status === 'OPEN')
        .toArray();
      return shifts[0] || null;
    } catch (err) {
      console.warn('Failed to get open station shift:', err);
      return null;
    }
  },

  async upsert(item) {
    try {
      await db.stationShifts.put(item);
    } catch (err) {
      console.warn('Failed to upsert station shift:', err);
    }
  },

  async bulkUpsert(items) {
    try {
      if (items && items.length > 0) await db.stationShifts.bulkPut(items);
    } catch (err) {
      console.warn('Failed to bulk upsert station shifts:', err);
    }
  },
};

export const stationExpensesDB = {
  async getByStationId(stationId) {
    try {
      return await db.stationExpenses
        .where('station_id')
        .equals(stationId)
        .reverse()
        .sortBy('expense_date');
    } catch (err) {
      console.warn('Failed to get station expenses:', err);
      return [];
    }
  },

  async upsert(item) {
    try {
      await db.stationExpenses.put(item);
    } catch (err) {
      console.warn('Failed to upsert station expense:', err);
    }
  },

  async bulkUpsert(items) {
    try {
      if (items && items.length > 0) await db.stationExpenses.bulkPut(items);
    } catch (err) {
      console.warn('Failed to bulk upsert station expenses:', err);
    }
  },
};

// Maps every name the backend may use for a collection (plural payload key, or the
// singular entity_type written by syncQueue.add) onto its local Dexie table.
const TABLE_BY_ENTITY_NAME = {
  trucks: 'trucks',
  truck: 'trucks',
  drivers: 'drivers',
  driver: 'drivers',
  companies: 'companies',
  company: 'companies',
  operations: 'operations',
  operation: 'operations',
  trips: 'trips',
  trip: 'trips',
  trip_updates: 'tripUpdates',
  trip_update: 'tripUpdates',
  clients: 'clients',
  client: 'clients',
  orders: 'orders',
  order: 'orders',
  invoices: 'invoices',
  invoice: 'invoices',
  stock_movements: 'stockMovements',
  stock_movement: 'stockMovements',
  pricing_rules: 'pricingRules',
  pricing_rule: 'pricingRules',
  stations: 'stations',
  station: 'stations',
  station_tanks: 'stationTanks',
  station_tank: 'stationTanks',
  station_pumps: 'stationPumps',
  station_pump: 'stationPumps',
  station_attendants: 'stationAttendants',
  station_attendant: 'stationAttendants',
  station_price_history: 'stationPrices',
  station_prices: 'stationPrices',
  station_price: 'stationPrices',
  station_purchases: 'stationPurchases',
  station_purchase: 'stationPurchases',
  station_sales: 'stationSales',
  station_sale: 'stationSales',
  station_shifts: 'stationShifts',
  station_shift: 'stationShifts',
  station_expenses: 'stationExpenses',
  station_expense: 'stationExpenses',
};

// Normalises whatever shape the backend sends for tombstones into
// { tableName: [id, ...] }. Two shapes are accepted:
//   - keyed:  { trucks: ["id1"], trips: [{ id: "id2" }] }
//   - flat:   [{ entity_type: "truck", id: "id1" }, ...]
const normalizeDeletions = (deletions) => {
  const byTable = {};
  const push = (name, rawId) => {
    const table = TABLE_BY_ENTITY_NAME[name];
    const id = rawId && typeof rawId === 'object' ? rawId.id ?? rawId.entity_id : rawId;
    if (!table || id === undefined || id === null) return;
    (byTable[table] = byTable[table] || []).push(id);
  };

  if (Array.isArray(deletions)) {
    deletions.forEach((entry) => {
      if (!entry) return;
      push(entry.entity_type || entry.entity || entry.type, entry.id ?? entry.entity_id);
    });
  } else if (deletions && typeof deletions === 'object') {
    Object.entries(deletions).forEach(([name, ids]) => {
      if (!Array.isArray(ids)) return;
      ids.forEach((id) => push(name, id));
    });
  }

  return byTable;
};

// Crochet appelé après chaque page enregistrée (modèle de lecture local).
// Un échec n'invalide jamais la page déjà validée.
let crochetDePage = null;

// Full sync operation
export const fullSync = {
  definirCrochetDePage(fonction) {
    crochetDePage = typeof fonction === 'function' ? fonction : null;
  },

  async saveAll(data) {
    try {
      const payload = data?.data ? data.data : data || {};
      await db.transaction(
        'rw',
        [
          db.trucks,
          db.drivers,
          db.companies,
          db.operations,
          db.trips,
          db.tripUpdates,
          db.clients,
          db.orders,
          db.invoices,
          db.stockMovements,
          db.pricingRules,
          db.stations,
          db.stationTanks,
          db.stationPumps,
          db.stationAttendants,
          db.stationPrices,
          db.stationPurchases,
          db.stationSales,
          db.stationShifts,
          db.stationExpenses,
        ],
        async () => {
          if (payload.trucks && payload.trucks.length > 0)
            await db.trucks.bulkPut(payload.trucks);
          if (payload.drivers && payload.drivers.length > 0)
            await db.drivers.bulkPut(payload.drivers);
          if (payload.companies && payload.companies.length > 0)
            await db.companies.bulkPut(payload.companies);
          if (payload.operations && payload.operations.length > 0)
            await db.operations.bulkPut(payload.operations);
          if (payload.trips && payload.trips.length > 0)
            await db.trips.bulkPut(payload.trips);
          if (payload.trip_updates && payload.trip_updates.length > 0)
            await db.tripUpdates.bulkPut(payload.trip_updates);
          if (payload.clients && payload.clients.length > 0)
            await db.clients.bulkPut(payload.clients);
          if (payload.orders && payload.orders.length > 0)
            await db.orders.bulkPut(payload.orders);
          if (payload.invoices && payload.invoices.length > 0)
            await db.invoices.bulkPut(payload.invoices);
          if (payload.stock_movements && payload.stock_movements.length > 0)
            await db.stockMovements.bulkPut(payload.stock_movements);
          if (payload.pricing_rules && payload.pricing_rules.length > 0)
            await db.pricingRules.bulkPut(payload.pricing_rules);
          if (payload.stations && payload.stations.length > 0)
            await db.stations.bulkPut(payload.stations);
          if (payload.station_tanks && payload.station_tanks.length > 0)
            await db.stationTanks.bulkPut(payload.station_tanks);
          if (payload.station_pumps && payload.station_pumps.length > 0)
            await db.stationPumps.bulkPut(payload.station_pumps);
          if (payload.station_attendants && payload.station_attendants.length > 0)
            await db.stationAttendants.bulkPut(payload.station_attendants);
          if (payload.station_price_history && payload.station_price_history.length > 0)
            await db.stationPrices.bulkPut(payload.station_price_history);
          if (payload.station_purchases && payload.station_purchases.length > 0)
            await db.stationPurchases.bulkPut(payload.station_purchases);
          if (payload.station_sales && payload.station_sales.length > 0)
            await db.stationSales.bulkPut(payload.station_sales);
          if (payload.station_shifts && payload.station_shifts.length > 0)
            await db.stationShifts.bulkPut(payload.station_shifts);
          if (payload.station_expenses && payload.station_expenses.length > 0)
            await db.stationExpenses.bulkPut(payload.station_expenses);

          // Tombstones: without them a record deleted at the office stays visible
          // forever in the local IndexedDB of every client.
          const deletions = normalizeDeletions(
            payload.deletions ?? payload.deleted ?? data?.deletions ?? data?.deleted,
          );
          for (const [tableName, ids] of Object.entries(deletions)) {
            if (ids.length > 0) await db.table(tableName).bulkDelete(ids);
          }
        },
      );

    } catch (err) {
      console.warn('Failed to save sync data:', err);
      throw err;
    }
    if (crochetDePage) {
      try {
        await crochetDePage(data);
      } catch (err) {
        console.warn('Local read model update failed:', err);
      }
    }
  },

  async commitCursor(timestamp) {
    // Do not use appState.set here: it intentionally swallows errors for
    // optional preferences, while a sync cursor is a durability guarantee.
    await db.appState.put({ key: 'lastSyncTimestamp', value: timestamp });
  },

  async getLastSyncTime() {
    try {
      return await appState.get('lastSyncTimestamp');
    } catch (err) {
      console.warn('Failed to get last sync time:', err);
      return null;
    }
  },
};

// Clear all data (for logout).
// The sync queue is NOT collateral damage: a dispatcher logging out while offline used
// to lose every write he made. By default we refuse to wipe a non-empty queue and let
// the caller decide (warn the user, sync first, or pass discardPendingQueue: true).
export const clearAllData = async ({ discardPendingQueue = false } = {}) => {
  const pendingCount = await syncQueue.countPending();
  if (pendingCount > 0 && !discardPendingQueue) {
    return { cleared: false, pendingCount };
  }

  try {
    await db.transaction(
      'rw',
      [
        db.trucks,
        db.drivers,
        db.companies,
        db.operations,
        db.trips,
        db.tripUpdates,
        db.clients,
        db.orders,
        db.invoices,
        db.stockMovements,
        db.pricingRules,
        db.stations,
        db.stationTanks,
        db.stationPumps,
        db.stationAttendants,
        db.stationPrices,
        db.stationPurchases,
        db.stationSales,
        db.stationShifts,
        db.stationExpenses,
        db.syncQueue,
        db.appState,
        db.records,
        db.httpCache,
        db.idMap,
        db.fichiers,
      ],
      async () => {
        await db.trucks.clear();
        await db.drivers.clear();
        await db.companies.clear();
        await db.operations.clear();
        await db.trips.clear();
        await db.tripUpdates.clear();
        await db.clients.clear();
        await db.orders.clear();
        await db.invoices.clear();
        await db.stockMovements.clear();
        await db.pricingRules.clear();
        await db.stations.clear();
        await db.stationTanks.clear();
        await db.stationPumps.clear();
        await db.stationAttendants.clear();
        await db.stationPrices.clear();
        await db.stationPurchases.clear();
        await db.stationSales.clear();
        await db.stationShifts.clear();
        await db.stationExpenses.clear();
        await db.syncQueue.clear();
        await db.appState.clear();
        await db.records.clear();
        await db.httpCache.clear();
        await db.idMap.clear();
        await db.fichiers.clear();
      },
    );
    return { cleared: true, pendingCount: 0 };
  } catch (err) {
    console.warn('Failed to clear all data:', err);
    return { cleared: false, pendingCount, error: err };
  }
};

export default db;
